import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import {
  getDashboardErrorMessage,
  useCreateDashboardMetricValueRevisionMutation,
  useDeleteDashboardMetricValueMutation,
  useGetDashboardSeriesQuery,
  useRestoreDashboardMetricValueMutation
} from './dashboardApi';
import DashboardApproverField from './DashboardApproverField';
import {
  DASHBOARD_APPROVAL_MODE,
  DASHBOARD_DATA_STATE
} from './dashboardContract';
import { canRemoveDashboardData } from './dashboardPermissions';
import {
  getDashboardMetricConstraintText,
  getDashboardMetricStep,
  validateDashboardMetricInput
} from './dashboardSubmissionModel';
import {
  DASHBOARD_VALUE_HISTORY_PAGE_SIZE,
  DASHBOARD_VALUE_REMOVAL_REASON_MAX_LENGTH,
  buildDashboardValueRevisionMessage,
  buildDashboardValueRows,
  createDashboardValueHistoryRange,
  paginateDashboardValueRows,
  validateDashboardValueHistoryRange,
  validateDashboardValueRemovalReason
} from './dashboardValueManagementModel';
import { formatDashboardMetricValue } from './dashboardViewModel';

const formatValue = (row) =>
  formatDashboardMetricValue(row.metric, {
    dataState: DASHBOARD_DATA_STATE.AVAILABLE,
    value: row.value
  });

const formatDate = (value) => dayjs(value).format('MMM D, YYYY');

const DashboardValueManager = ({ locationId, metrics = [] }) => {
  const mayRemove = canRemoveDashboardData();
  const [draftRange, setDraftRange] = useState(() =>
    createDashboardValueHistoryRange()
  );
  const [appliedRange, setAppliedRange] = useState(() =>
    createDashboardValueHistoryRange()
  );
  const [rangeError, setRangeError] = useState(null);
  const [page, setPage] = useState(1);
  const [editRow, setEditRow] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editValueError, setEditValueError] = useState(null);
  const [approver, setApprover] = useState(null);
  const [approverError, setApproverError] = useState(null);
  const [removeRow, setRemoveRow] = useState(null);
  const [removalReason, setRemovalReason] = useState('');
  const [removalReasonError, setRemovalReasonError] = useState(null);
  const [recentlyRemoved, setRecentlyRemoved] = useState(null);
  const [notification, setNotification] = useState(null);

  const seriesQuery = useGetDashboardSeriesQuery(
    {
      locationId,
      fromDate: appliedRange.fromDate,
      toDate: appliedRange.toDate
    },
    { skip: !locationId }
  );
  const [createRevision, createRevisionState] =
    useCreateDashboardMetricValueRevisionMutation();
  const [deleteMetricValue, deleteState] =
    useDeleteDashboardMetricValueMutation();
  const [restoreMetricValue, restoreState] =
    useRestoreDashboardMetricValueMutation();

  const rows = useMemo(
    () => buildDashboardValueRows(seriesQuery.data?.metrics, metrics),
    [metrics, seriesQuery.data?.metrics]
  );
  const pageCount = Math.max(
    1,
    Math.ceil(rows.length / DASHBOARD_VALUE_HISTORY_PAGE_SIZE)
  );
  const visibleRows = paginateDashboardValueRows(rows, page);

  useEffect(() => {
    setPage(1);
    setEditRow(null);
    setRemoveRow(null);
    setRecentlyRemoved(null);
    setNotification(null);
  }, [locationId]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const applyRange = (event) => {
    event.preventDefault();
    const error = validateDashboardValueHistoryRange(draftRange);
    setRangeError(error);
    if (error) return;

    setAppliedRange({ ...draftRange });
    setPage(1);
    setRecentlyRemoved(null);
    setNotification(null);
  };

  const openEditDialog = (row) => {
    setEditRow(row);
    setEditValue(String(row.value));
    setEditValueError(null);
    setApprover(null);
    setApproverError(null);
    setRecentlyRemoved(null);
    setNotification(null);
    createRevisionState.reset?.();
  };

  const saveCorrection = async () => {
    const error = validateDashboardMetricInput(
      { ...editRow.metric, isRequired: true },
      editValue
    );
    setEditValueError(error);
    if (error) return;
    const requiresApproval =
      editRow.metric.approvalMode === DASHBOARD_APPROVAL_MODE.REQUIRED;
    if (requiresApproval && !approver?.user_id) {
      setApproverError('Select who should receive the approval email.');
      return;
    }

    try {
      const revision = await createRevision({
        metricValueId: editRow.metricValueId,
        value: Number(editValue),
        approverUserId: requiresApproval ? approver.user_id : null
      }).unwrap();
      setNotification({
        severity:
          revision.valueStatus === 'pending_approval' ? 'info' : 'success',
        text: buildDashboardValueRevisionMessage(revision)
      });
      setEditRow(null);
    } catch (_error) {
      // RTK Query exposes the normalized error through createRevisionState.
    }
  };

  const openRemoveDialog = (row) => {
    setRemoveRow(row);
    setRemovalReason('');
    setRemovalReasonError(null);
    setRecentlyRemoved(null);
    setNotification(null);
    deleteState.reset?.();
  };

  const removeValue = async () => {
    const error = validateDashboardValueRemovalReason(removalReason);
    setRemovalReasonError(error);
    if (error) return;

    try {
      const result = await deleteMetricValue({
        metricValueId: removeRow.metricValueId,
        reason: removalReason.trim()
      }).unwrap();
      setRecentlyRemoved(result.canRestore ? { result, row: removeRow } : null);
      setNotification({
        severity: 'success',
        text:
          removeRow.metric.label +
          ' for ' +
          formatDate(removeRow.effectiveDate) +
          ' was removed from the dashboard.'
      });
      setRemoveRow(null);
    } catch (_error) {
      // RTK Query exposes the normalized error through deleteState.
    }
  };

  const undoRemoval = async () => {
    if (!recentlyRemoved) return;

    try {
      await restoreMetricValue(recentlyRemoved.result.metricValueId).unwrap();
      setNotification({
        severity: 'success',
        text:
          recentlyRemoved.row.metric.label +
          ' for ' +
          formatDate(recentlyRemoved.row.effectiveDate) +
          ' was restored.'
      });
      setRecentlyRemoved(null);
    } catch (_error) {
      // RTK Query exposes the normalized error through restoreState.
    }
  };

  return (
    <Paper
      data-testid="dashboard-value-manager"
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        mt: 3,
        p: { xs: 2, md: 3 }
      }}
    >
      <Stack
        alignItems={{ md: 'flex-start' }}
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography component="h2" fontSize={20} fontWeight={700}>
            Manage submitted values
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Review approved values, submit corrections, or remove an incorrect
            value. Corrections still follow each metric&apos;s approval policy.
          </Typography>
        </Box>
        <Button
          disabled={seriesQuery.isFetching}
          onClick={() => seriesQuery.refetch?.()}
          size="small"
          variant="outlined"
        >
          Refresh
        </Button>
      </Stack>

      <Stack
        alignItems={{ md: 'flex-start' }}
        component="form"
        direction={{ xs: 'column', md: 'row' }}
        noValidate
        onSubmit={applyRange}
        spacing={2}
        sx={{ mt: 2 }}
      >
        <TextField
          InputLabelProps={{ shrink: true }}
          label="Values from"
          onChange={(event) => {
            setDraftRange((current) => ({
              ...current,
              fromDate: event.target.value
            }));
            setRangeError(null);
          }}
          size="small"
          type="date"
          value={draftRange.fromDate}
        />
        <TextField
          InputLabelProps={{ shrink: true }}
          label="Values to"
          onChange={(event) => {
            setDraftRange((current) => ({
              ...current,
              toDate: event.target.value
            }));
            setRangeError(null);
          }}
          size="small"
          type="date"
          value={draftRange.toDate}
        />
        <Button type="submit" variant="outlined">
          Apply dates
        </Button>
      </Stack>
      {rangeError && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {rangeError}
        </Alert>
      )}

      {notification && (
        <Alert
          action={
            recentlyRemoved ? (
              <Button
                color="inherit"
                disabled={restoreState.isLoading}
                onClick={undoRemoval}
                size="small"
              >
                Undo removal
              </Button>
            ) : undefined
          }
          severity={notification.severity}
          sx={{ mt: 2 }}
        >
          {notification.text}
        </Alert>
      )}
      {restoreState.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getDashboardErrorMessage(
            restoreState.error,
            'The dashboard value could not be restored.'
          )}
        </Alert>
      )}

      {seriesQuery.isLoading ? (
        <Stack alignItems="center" sx={{ py: 5 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : seriesQuery.error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getDashboardErrorMessage(
            seriesQuery.error,
            'Submitted values could not be loaded.'
          )}
        </Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No approved values were found in this date range.
        </Alert>
      ) : (
        <>
          <TableContainer sx={{ mt: 2 }}>
            <Table aria-label="Submitted dashboard values" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Metric</TableCell>
                  <TableCell>Approved value</TableCell>
                  <TableCell>Correction policy</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow key={row.metricValueId}>
                    <TableCell>{formatDate(row.effectiveDate)}</TableCell>
                    <TableCell>
                      <Typography fontWeight={600} variant="body2">
                        {row.metric.label}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatValue(row)}</TableCell>
                    <TableCell>
                      <Chip
                        color={
                          row.metric.approvalMode ===
                          DASHBOARD_APPROVAL_MODE.REQUIRED
                            ? 'warning'
                            : 'default'
                        }
                        label={
                          row.metric.approvalMode ===
                          DASHBOARD_APPROVAL_MODE.REQUIRED
                            ? 'Approval required'
                            : 'Immediate'
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={1}
                      >
                        <Button
                          aria-label={
                            'Edit ' +
                            row.metric.label +
                            ' value for ' +
                            row.effectiveDate
                          }
                          onClick={() => openEditDialog(row)}
                          size="small"
                        >
                          Edit
                        </Button>
                        {mayRemove && (
                          <Button
                            aria-label={
                              'Remove ' +
                              row.metric.label +
                              ' value for ' +
                              row.effectiveDate
                            }
                            color="error"
                            onClick={() => openRemoveDialog(row)}
                            size="small"
                          >
                            Remove
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {pageCount > 1 && (
            <Stack alignItems="center" sx={{ mt: 2 }}>
              <Pagination
                count={pageCount}
                onChange={(_event, nextPage) => setPage(nextPage)}
                page={page}
              />
            </Stack>
          )}
        </>
      )}

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => setEditRow(null)}
        open={Boolean(editRow)}
      >
        <DialogTitle>
          {editRow ? 'Correct ' + editRow.metric.label : 'Correct value'}
        </DialogTitle>
        <DialogContent>
          {editRow && (
            <>
              <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
                The approved value for {formatDate(editRow.effectiveDate)} is{' '}
                {formatValue(editRow)}.
              </Typography>
              <TextField
                autoFocus
                error={Boolean(editValueError)}
                fullWidth
                helperText={
                  editValueError ||
                  getDashboardMetricConstraintText(editRow.metric)
                }
                inputProps={{
                  max: editRow.metric.maximum ?? undefined,
                  min: editRow.metric.minimum ?? undefined,
                  step: getDashboardMetricStep(editRow.metric)
                }}
                label="New value"
                onChange={(event) => {
                  setEditValue(event.target.value);
                  setEditValueError(null);
                }}
                sx={{ mt: 1 }}
                type="number"
                value={editValue}
              />
              {editRow.metric.approvalMode ===
                DASHBOARD_APPROVAL_MODE.REQUIRED && (
                <Box sx={{ mt: 2 }}>
                  <DashboardApproverField
                    approver={approver}
                    error={approverError}
                    locationId={locationId}
                    onChange={(value) => {
                      setApprover(value);
                      setApproverError(null);
                    }}
                  />
                </Box>
              )}
              {createRevisionState.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {getDashboardErrorMessage(
                    createRevisionState.error,
                    'The correction could not be submitted.'
                  )}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={createRevisionState.isLoading}
            onClick={() => setEditRow(null)}
          >
            Cancel
          </Button>
          <Button
            disabled={createRevisionState.isLoading}
            onClick={saveCorrection}
            variant="contained"
          >
            {createRevisionState.isLoading ? 'Saving…' : 'Save correction'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => setRemoveRow(null)}
        open={Boolean(removeRow)}
      >
        <DialogTitle>
          {removeRow
            ? 'Remove ' + removeRow.metric.label + ' value?'
            : 'Remove value?'}
        </DialogTitle>
        <DialogContent>
          {removeRow && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This removes the approved value for{' '}
                {formatDate(removeRow.effectiveDate)} from dashboard totals,
                widgets, and charts.
              </Alert>
              <TextField
                autoFocus
                error={Boolean(removalReasonError)}
                fullWidth
                helperText={
                  removalReasonError ||
                  removalReason.length +
                    '/' +
                    DASHBOARD_VALUE_REMOVAL_REASON_MAX_LENGTH +
                    ' characters'
                }
                inputProps={{
                  maxLength: DASHBOARD_VALUE_REMOVAL_REASON_MAX_LENGTH
                }}
                label="Reason for removal"
                minRows={3}
                multiline
                onChange={(event) => {
                  setRemovalReason(event.target.value);
                  setRemovalReasonError(null);
                }}
                required
                value={removalReason}
              />
              {deleteState.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {getDashboardErrorMessage(
                    deleteState.error,
                    'The dashboard value could not be removed.'
                  )}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deleteState.isLoading}
            onClick={() => setRemoveRow(null)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={deleteState.isLoading}
            onClick={removeValue}
            variant="contained"
          >
            {deleteState.isLoading ? 'Removing…' : 'Remove value'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DashboardValueManager;
